import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import {
  dispatchesAPI,
  DispatchRiderSnapshot,
  UnbatchableItem,
} from '@/services/api/dispatches';

interface ForceAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  dispatchId: string;
  /**
   * The dispatch's own riders (`ridersSnapshot`) — the only valid targets. A
   * forced batch is one more trip for a rider already on this run, not a way
   * to bring a new rider in (that's Add riders).
   */
  riders: DispatchRiderSnapshot[];
  /** Trips each rider already carries, for the "sudah N trip" hint. */
  tripCountByRiderId?: Record<number, number>;
  onSubmit: (
    itemIds: string[],
    rider: DispatchRiderSnapshot
  ) => Promise<void>;
}

/**
 * Rescue for items the planner refused as OVERSIZED_FOR_CAP (single item over
 * the run's weight/volume caps — e.g. a 240 kg shipment): pick the items +
 * one of the dispatch's riders, and the BE creates a dedicated `forced` batch
 * for them with the capacity caps deliberately bypassed. Manual, explicit,
 * audited.
 */
const ForceAssignModal: React.FC<ForceAssignModalProps> = ({
  isOpen,
  onClose,
  dispatchId,
  riders,
  tripCountByRiderId,
  onSubmit,
}) => {
  const [items, setItems] = useState<UnbatchableItem[]>([]);
  const [caps, setCaps] = useState<{ kg: number; m3: number } | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  // Oversized items are rare — default ALL checked so the common case is
  // "pick rider, submit".
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [rider, setRider] = useState<DispatchRiderSnapshot | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // One rider on the run → nothing to choose; preselect them.
    setRider(riders.length === 1 ? riders[0] : null);
    let cancelled = false;
    setItemsLoading(true);
    dispatchesAPI
      .unbatchable(dispatchId)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setCaps({ kg: res.maxWeightKg, m3: res.maxVolumeM3 });
        setChecked(
          Object.fromEntries(res.items.map((i) => [i.itemId, true]))
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        toast.error('Could not load oversized items', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, dispatchId, riders]);

  const selectedIds = useMemo(
    () => items.filter((i) => checked[i.itemId]).map((i) => i.itemId),
    [items, checked]
  );
  const selectedKg = useMemo(
    () =>
      items
        .filter((i) => checked[i.itemId])
        .reduce((sum, i) => sum + i.weightKg, 0),
    [items, checked]
  );
  const canSubmit = !submitting && selectedIds.length > 0 && rider != null;

  const handleSubmit = async () => {
    if (!canSubmit || !rider) return;
    setSubmitting(true);
    try {
      await onSubmit(selectedIds, rider);
    } finally {
      setSubmitting(false);
    }
  };

  const capLabel = (item: UnbatchableItem) =>
    item.overWeight
      ? `${item.weightKg.toLocaleString('id-ID')} kg > cap ${caps?.kg ?? '—'} kg`
      : `${item.volumeM3.toLocaleString('id-ID')} m³ > cap ${caps?.m3 ?? '—'} m³`;

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-scales-3-line" />}
      iconTone="warning"
      title="Force assign — barang oversized"
      subtitle="Item di bawah melebihi batas berat/volume per batch, jadi planner tidak pernah bisa menempatkannya. Pilih salah satu rider dispatch ini: sistem membuat batch khusus (ditandai FORCED) dengan batas kapasitas sengaja dilewati."
      size="lg"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-text-sub-600">
            {selectedIds.length} item · {selectedKg.toLocaleString('id-ID')} kg
            {rider ? ` → ${rider.name}` : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? 'Assigning…' : 'Force assign'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* ── Items over the caps ─────────────────────────────────────── */}
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Item oversized{itemsLoading ? '' : ` (${items.length})`}
          </p>
          <div className="max-h-[220px] overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
            {itemsLoading ? (
              <div className="px-3 py-4 text-center text-xs text-text-sub-600">
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-text-sub-600">
                Tidak ada item oversized yang bisa di-force — mungkin sudah
                di-assign di dispatch ini atau diklaim dispatch lain.
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={item.itemId}
                  type="button"
                  onClick={() =>
                    setChecked((prev) => ({
                      ...prev,
                      [item.itemId]: !prev[item.itemId],
                    }))
                  }
                  disabled={submitting}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                    checked[item.itemId] ? 'bg-bg-weak-50' : 'hover:bg-bg-weak-50'
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                      {item.itemName || item.waybill}
                      <span className="ml-2 font-mono text-[11px] text-text-sub-600">
                        {item.bookingId}
                      </span>
                    </span>
                    <span className="block text-[11px] text-text-sub-600 truncate">
                      {item.receiverName} · {item.destinationAddress}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">
                    {capLabel(item)}
                  </span>
                  <i
                    className={`${
                      checked[item.itemId]
                        ? 'ri-checkbox-fill text-accent'
                        : 'ri-checkbox-blank-line text-text-soft-400'
                    } text-[16px] shrink-0`}
                  />
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Rider (single pick, dispatch riders only) ───────────────── */}
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Assign ke rider dispatch ini ({riders.length})
          </p>
          <div className="max-h-[220px] min-h-[64px] overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
            {riders.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-text-sub-600">
                Dispatch ini belum punya rider — tambahkan dulu lewat{' '}
                <span className="font-semibold text-text-strong-950">
                  Add riders
                </span>
                , baru force assign.
              </div>
            ) : (
              riders.map((r) => {
                const isSelected = rider?.id === r.id;
                const trips = tripCountByRiderId?.[r.id] ?? 0;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRider(r)}
                    disabled={submitting}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                      isSelected ? 'bg-bg-weak-50' : 'hover:bg-bg-weak-50'
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                        {r.name}
                      </span>
                      <span className="flex items-center gap-2 font-mono text-[11px] text-text-sub-600 tabular-nums">
                        <span>{r.code}</span>
                        <span aria-hidden="true">·</span>
                        <span>
                          {trips === 0
                            ? 'belum ada trip'
                            : `${trips} trip${trips === 1 ? '' : 's'}`}
                        </span>
                      </span>
                    </span>
                    <i
                      className={`${
                        isSelected
                          ? 'ri-radio-button-fill text-accent'
                          : 'ri-checkbox-blank-circle-line text-text-soft-400'
                      } text-[16px] shrink-0`}
                    />
                  </button>
                );
              })
            )}
          </div>
          <p className="mt-1.5 text-[11px] text-text-soft-400">
            Hanya rider yang sudah ada di dispatch ini. Muatan melebihi
            kapasitas normal — pastikan kendaraannya sanggup.
          </p>
        </div>
      </div>
    </FormModal>
  );
};

export default ForceAssignModal;
